import api from './api';
import type {
  PaginatedResponse,
  ByInsurerRow,
  ByProductRow,
  ByPospRow,
  ByPospSummary,
  PospOutstandingRow,
  PospOutstandingSummary,
  ReportGroupSummary,
  LifecyclePipelineResponse,
  ProfitabilitySummary,
} from '@/types';

// ─── Filters ────────────────────────────────────────────────────────────────

export interface ReportFilters {
  period?: string;
  insurer?: string;
  /** Explicit window on policy_date. An explicit window OVERRIDES ?period
   *  server-side (they are not intersected), so the two controls cannot combine
   *  into a silently empty report. All four reports honour it. */
  date_from?: string;
  date_to?: string;
}

/** One policy behind an insurer row — GET /commissions/reports/by-insurer/policies/.
 *
 *  ROLE-SHAPED: the money keys are gated server-side (apps/commissions/visibility.py).
 *  A broker gets total/posp/platform/broker/margin; a POSP gets ONLY
 *  `your_commission` and the broker-side keys are ABSENT — not zeroed. Nothing here
 *  may be assumed present: render only what the payload actually carries.
 */
export interface InsurerPolicyRow {
  id: string | null;
  /** Our internal reference, e.g. POL-716-godigit-260807-F798C6. */
  policy_number: string;
  /** The number the insurer issued, e.g. D210524290. Blank on the minority of
   *  policies that have none; render `policy_number` in that case. */
  insurer_policy_number?: string;
  holder_name: string;
  policy_date: string | null;
  gross_premium: string;
  posp_name: string;
  // ── broker-side only ──
  total_commission?: string;
  posp_commission?: string;
  platform_commission?: string;
  broker_commission?: string;
  margin?: string;
  // ── POSP only ──
  your_commission?: string;
}

/** The drill-down summary block — describes the WHOLE filtered set, not the page.
 *  Same role gate as the rows (note: the broker's "given" figure is `paid_to_posp`
 *  here, not `given` as in the grouping reports). */
export interface InsurerPoliciesSummary {
  received?: string;
  paid_to_posp?: string;
  paid_to_advertiser?: string;
  broker?: string;
  margin?: string;
  your_commission?: string;
  downline_commission?: string;
  policy_count: number;
}

export type InsurerPoliciesResponse = PaginatedResponse<InsurerPolicyRow> & {
  summary?: InsurerPoliciesSummary;
};

// Paginated report responses carry an optional summary block alongside the
// standard DRF page envelope (count/next/previous/results).
export type ByInsurerResponse = PaginatedResponse<ByInsurerRow> & {
  summary?: ReportGroupSummary;
};
export type ByProductResponse = PaginatedResponse<ByProductRow> & {
  summary?: ReportGroupSummary;
};
export type ByPospResponse = PaginatedResponse<ByPospRow> & {
  summary?: ByPospSummary;
};
export type PospOutstandingResponse = PaginatedResponse<PospOutstandingRow> & {
  summary?: PospOutstandingSummary;
};

function buildParams(page: number | undefined, filters?: ReportFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (page != null) params.append('page', String(page));
  if (filters?.period) params.append('period', filters.period);
  if (filters?.insurer) params.append('insurer', filters.insurer);
  if (filters?.date_from) params.append('date_from', filters.date_from);
  if (filters?.date_to) params.append('date_to', filters.date_to);
  return params;
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const commissionReportsService = {
  // By Insurance Company
  async getByInsurer(page = 1, filters?: ReportFilters): Promise<ByInsurerResponse> {
    const res = await api.get<ByInsurerResponse>(
      `/commissions/reports/by-insurer/?${buildParams(page, filters)}`,
    );
    return res.data;
  },

  // By Insurance Company — drill-down: one insurer's policies, with their splits.
  // Really paginated (per-policy and unbounded), unlike the grouping reports.
  // `insurer` is required by the endpoint; it overrides any insurer already in
  // `filters` so the drill-down always asks about the row that was clicked.
  async getPoliciesByInsurer(
    insurerId: string,
    page = 1,
    filters?: ReportFilters,
  ): Promise<InsurerPoliciesResponse> {
    const params = buildParams(page, { ...filters, insurer: insurerId });
    const res = await api.get<InsurerPoliciesResponse>(
      `/commissions/reports/by-insurer/policies/?${params}`,
    );
    return res.data;
  },

  // By Policy Type / Product
  async getByProduct(page = 1, filters?: ReportFilters): Promise<ByProductResponse> {
    const res = await api.get<ByProductResponse>(
      `/commissions/reports/by-product/?${buildParams(page, filters)}`,
    );
    return res.data;
  },

  // By POSP (Agent)
  async getByPosp(page = 1, filters?: ReportFilters): Promise<ByPospResponse> {
    const res = await api.get<ByPospResponse>(
      `/commissions/reports/by-posp/?${buildParams(page, filters)}`,
    );
    return res.data;
  },

  // Outstanding owed per POSP (item 7 step 3) — approved-but-unpaid commission,
  // i.e. what a payout batch created right now would sweep up for that POSP.
  async getPospOutstanding(filters?: ReportFilters): Promise<PospOutstandingResponse> {
    const res = await api.get<PospOutstandingResponse>(
      `/commissions/reports/posp-outstanding/?${buildParams(undefined, filters)}`,
    );
    return res.data;
  },

  // Lifecycle Pipeline + Payout Register (not paginated)
  async getLifecyclePipeline(filters?: ReportFilters): Promise<LifecyclePipelineResponse> {
    const res = await api.get<LifecyclePipelineResponse>(
      `/commissions/reports/lifecycle/?${buildParams(undefined, filters)}`,
    );
    return res.data;
  },

  // Profitability — "is the company making money?". BROKER_ADMIN ONLY server-side
  // (403 for every other role, including broker_employee); the caller must also
  // gate the entry point client-side (see ReportsTab.tsx) so the tab/query never
  // even renders for anyone else.
  async getProfitability(filters?: ReportFilters): Promise<ProfitabilitySummary> {
    const res = await api.get<ProfitabilitySummary>(
      `/commissions/reports/profitability/?${buildParams(undefined, filters)}`,
    );
    return res.data;
  },

  // ─── CSV Exports (format=csv -> blob download) ──────────────────────────────

  async exportByInsurer(filters?: ReportFilters): Promise<Blob> {
    const params = buildParams(undefined, filters);
    params.append('format', 'csv');
    const res = await api.get(`/commissions/reports/by-insurer/?${params}`, {
      responseType: 'blob',
    });
    return res.data;
  },

  // The drill-down export is deliberately NOT paginated server-side (one page of
  // an export is useless) but runs over the same scoped + filtered queryset.
  async exportPoliciesByInsurer(insurerId: string, filters?: ReportFilters): Promise<Blob> {
    const params = buildParams(undefined, { ...filters, insurer: insurerId });
    params.append('format', 'csv');
    const res = await api.get(`/commissions/reports/by-insurer/policies/?${params}`, {
      responseType: 'blob',
    });
    return res.data;
  },

  // By POSP → drill-down: one agent's policies. Reuses
  // GET /brokers/posps/{id}/policies/ (NOT a /commissions/reports/ route — see
  // PospPoliciesDrilldown in ReportsTab.tsx and POSPViewSet.policies in
  // apps/brokers/views.py), unpaginated, mirroring exportPoliciesByInsurer.
  // period + date_from/date_to are honoured by that endpoint (same windows as
  // by-insurer); insurer is not, so it is omitted.
  async exportPoliciesByPosp(pospId: string, filters?: ReportFilters): Promise<Blob> {
    const params = new URLSearchParams();
    if (filters?.period) params.append('period', filters.period);
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    params.append('format', 'csv');
    const res = await api.get(`/brokers/posps/${pospId}/policies/?${params}`, {
      responseType: 'blob',
    });
    return res.data;
  },

  async exportByProduct(filters?: ReportFilters): Promise<Blob> {
    const params = buildParams(undefined, filters);
    params.append('format', 'csv');
    const res = await api.get(`/commissions/reports/by-product/?${params}`, {
      responseType: 'blob',
    });
    return res.data;
  },

  async exportByPosp(filters?: ReportFilters): Promise<Blob> {
    const params = buildParams(undefined, filters);
    params.append('format', 'csv');
    const res = await api.get(`/commissions/reports/by-posp/?${params}`, {
      responseType: 'blob',
    });
    return res.data;
  },

  async exportLifecyclePipeline(filters?: ReportFilters): Promise<Blob> {
    const params = buildParams(undefined, filters);
    params.append('format', 'csv');
    const res = await api.get(`/commissions/reports/lifecycle/?${params}`, {
      responseType: 'blob',
    });
    return res.data;
  },
};
